import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@pushpress/pushpress-ui";
import { CheckCircle2 } from "lucide-react";
import {
  DATA_NOT_AVAILABLE,
  STRIPE_DASHBOARD_URL,
  EMPTY_DATA,
} from "../../../constants";
import type { MemberDataType, StripePaymentData } from "../../../types";

export function getMatchingRecordData(data: string): {
  reason: string;
  confidenceScore: string;
} {
  try {
    const parsedData = JSON.parse(data);
    return {
      reason: parsedData.reason || EMPTY_DATA,
      confidenceScore: parsedData.confidenceScore || EMPTY_DATA,
    };
  } catch (error) {
    return {
      reason: EMPTY_DATA,
      confidenceScore: EMPTY_DATA,
    };
  }
}

export default function MatchingRecord({
  member,
  stripeBillingData,
}: {
  member: MemberDataType | null;
  stripeBillingData: StripePaymentData | string;
}) {
  const { reason, confidenceScore } = getMatchingRecordData(
    member?.matchingFeedback || "{}",
  );

  if (!member || !member.stripeId) {
    return <p className="tw-mb-4">{DATA_NOT_AVAILABLE}</p>;
  }

  const stripeEmail =
    typeof stripeBillingData === "string"
      ? DATA_NOT_AVAILABLE
      : stripeBillingData?.[member.stripeId]?.customerData?.email ||
        DATA_NOT_AVAILABLE;

  return (
    <Card>
      <CardHeader className="tw-pb-2">
        <div className="tw-flex tw-items-center tw-gap-2">
          <CheckCircle2 className="tw-h-5 tw-w-5 tw-text-green-500" />
          <CardTitle className="tw-text-lg tw-font-medium tw-text-neutral-900">
            Found a matching record
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="tw-bg-neutral-100 tw-rounded-lg tw-p-4 tw-border tw-border-neutral-100">
          <p className="tw-text-neutral-700 tw-leading-relaxed">{reason}</p>
        </div>

        <div className="grid gap-4">
          <div className="tw-flex tw-items-center tw-justify-between tw-py-2">
            <span className="tw-text-sm tw-font-medium tw-text-neutral-500">
              Stripe ID
            </span>
            <code className="tw-bg-neutral-100 tw-px-2 tw-py-1 tw-rounded tw-text-sm tw-font-mono tw-text-neutral-600">
              <a
                href={`${STRIPE_DASHBOARD_URL}${member.stripeId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="tw-text-blue-500"
              >
                {member.stripeId}
              </a>
            </code>
          </div>
          <div className="tw-flex tw-items-center tw-justify-between tw-pb-2">
            <span className="tw-text-sm tw-font-medium tw-text-neutral-500">
              Email
            </span>
            <code className="tw-bg-neutral-100 tw-px-2 tw-py-1 tw-rounded tw-text-sm tw-font-mono tw-text-neutral-700">
              {member.email || DATA_NOT_AVAILABLE}
            </code>
          </div>
          <div className="tw-flex tw-items-center tw-justify-between tw-pb-2">
            <span className="tw-text-sm tw-font-medium tw-text-neutral-500">
              Stripe Email
            </span>
            <code className="tw-bg-neutral-100 tw-px-2 tw-py-1 tw-rounded tw-text-sm tw-font-mono tw-text-neutral-700">
              {stripeEmail}
            </code>
          </div>
          <div className="tw-flex tw-items-center tw-justify-between">
            <span className="tw-text-sm tw-font-medium tw-text-neutral-500">
              Match Confidence
            </span>
            <Badge
              variant="secondary"
              className="tw-bg-green-50 tw-text-green-700 hover:tw-bg-green-50"
            >
              {confidenceScore}
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
